import React, { FunctionComponent, useContext, useCallback } from 'react';
import { PageHeader, Form, Group, ResponsivePadding } from '../../components';
import { BusinessFields } from './BusinessFields';
import {
  useToaster,
  useAuthToken,
  useNextApplicationPath,
  useRedirectToNextApplicationPathIfPageIsHidden,
  useWriteableFieldFormValidation,
} from '../../hooks';
import {
  RegistrationStore,
  updateRegistration,
  AuthStore,
  updateAuthMerchant,
  BrandStore,
} from '../../context';
import { BusinessFieldsValues } from './BusinessFields.types';
import {
  mapFormValuesToPayload,
  mapFormValuesToTeamPayload,
  mapRegistrationToInitialFormValues,
  validateFormValues,
  formMutators,
} from './BusinessFields.util';
import { coreapi } from '../../api';
import { history } from '../../history';
import { PrimaryButton } from '@stax-core/truffle-components';
import { Prompt } from 'react-router-dom';

export const BusinessPage: FunctionComponent = () => {
  const { toaster, toast } = useToaster();
  const { authToken } = useAuthToken();

  const nextPath = useNextApplicationPath();
  const {
    dispatch,
    state: { registration },
  } = useContext(RegistrationStore);
  const { dispatch: authDispatch } = useContext(AuthStore);
  const {
    state: { brand },
  } = useContext(BrandStore);

  const { regBusinessHeader, regBusinessBody } = brand || {};

  const onSubmit = useCallback(
    async (formValues: BusinessFieldsValues) => {
      const payload = mapFormValuesToPayload(formValues, registration);
      const teamPayload = mapFormValuesToTeamPayload(formValues);

      try {
        const r = await coreapi.put(authToken!, '/team/registration', payload);
        dispatch(updateRegistration(r));

        const m = await coreapi.put(authToken!, '/team', teamPayload);
        authDispatch(updateAuthMerchant(m));

        history.push(nextPath!);
      } catch (error) {
        toaster(toast.error(error, 'There was a problem saving your changes.'));
      }
    },
    [authToken, authDispatch, dispatch, toast, toaster, nextPath],
  );

  useRedirectToNextApplicationPathIfPageIsHidden('Business');

  const validate = useWriteableFieldFormValidation(validateFormValues);

  return (
    <>
      <PageHeader
        header={regBusinessHeader}
        id="business-page-header"
        data-testid="business-page-header"
        description={<p>{regBusinessBody}</p>}
      />

      <Form
        initialValues={mapRegistrationToInitialFormValues(registration)}
        validate={validate}
        mutators={formMutators}
        onSubmit={onSubmit}
      >
        {formProps => (
          <BusinessFields mode="edit" {...formProps}>
            <Prompt
              when={formProps.dirty && !formProps.submitting}
              message="You have unsaved changes; are you sure you want to leave without saving?"
            />
            <ResponsivePadding>
              <Group justify="end">
                <PrimaryButton
                  type="submit"
                  style={{ width: '100%' }}
                  id="business-continue-button"
                  data-testid="business-continue-button"
                  trackingId="business-continue-button"
                  disabled={formProps.submitting}
                >
                  <span>
                    {formProps.submitting ? 'Saving...' : 'Save & Continue'}
                  </span>
                </PrimaryButton>
              </Group>
            </ResponsivePadding>
          </BusinessFields>
        )}
      </Form>
    </>
  );
};
